/**
 * cs-CZ formatters used across modules.
 *
 * Number group separator is a non-breaking space (U+00A0) — matches the legacy
 * rendering and how the artboards space prices like `1 285 000 Kč`.
 */

const NBSP = ' ';

const intFormatter = new Intl.NumberFormat('cs-CZ', {
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat('cs-CZ', {
  maximumFractionDigits: 2,
});

const moneyFormatter = new Intl.NumberFormat('cs-CZ', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Group an integer with non-breaking spaces. `null`/`0`/empty → `'0'`. */
export function formatInt(value: number | string | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '0';
  return intFormatter.format(num).replace(/\s/g, NBSP);
}

/** Like `formatInt` but allows up to 2 decimal places. */
export function formatDecimal(value: number | string | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '0';
  return decimalFormatter.format(num).replace(/\s/g, NBSP);
}

/**
 * Always-two-decimals money formatter — mirrors the legacy `formatCzk` so the DPH recapitulation shows haléře
 * (`269 850,00`) while the rounded grand total stays a clean integer.
 */
export function formatMoney(value: number | string | null | undefined): string {
  if (value == null || value === '') return '0,00';
  const num = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(num)) return '0,00';
  return moneyFormatter.format(num).replace(/\s/g, NBSP);
}

/**
 * A stock car's mileage for display. `stav_km` is a free-text column (`45000`, `45 000`, `45 000 km`,
 * `cca 45 000`, sometimes a note): a plain number is regrouped, text with digits keeps its words and gets
 * the unit when it has none, text without digits is shown as authored. `formatInt` alone would turn all
 * of those into `0`. null = nothing to show.
 */
export function formatKm(raw: string | null | undefined): string | null {
  const text = (raw ?? '').trim();
  if (!text) return null;
  const digits = text.replace(/[\s\u00a0]/g, '').replace(/km$/i, '');
  if (/^\d+$/.test(digits)) return `${formatInt(digits)} km`;
  if (/\d/.test(text) && !/km\b/i.test(text)) return `${text} km`;
  return text;
}

/**
 * PHP-`floatval` lookalike — parses the leading number from free-text values
 * (`'1 %'` → 1, `'1.50 Kč'` → 1.5, `'12 500'` → 12). Legacy varchar columns
 * hold unit-suffixed strings that `Number()` turns into NaN, which printed as
 * 0 on documents. Matches floatval's behavior for space-grouped values too.
 */
export function parseFloatLoose(value: string | number | null | undefined): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value == null) return 0;
  const m = value.trim().match(/^-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : 0;
}

/** Czech short date (`12. 4. 2026`). Returns empty string for null/invalid. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d < new Date('2000-07-01')) return '';
  return `${d.getDate()}.${NBSP}${d.getMonth() + 1}.${NBSP}${d.getFullYear()}`;
}

/**
 * Czech declension for count subjects.
 *
 *   pluralCs(1, ['nalezen', 'nalezeny', 'nalezeno']) → 'nalezen'
 *   pluralCs(3, ['nalezen', 'nalezeny', 'nalezeno']) → 'nalezeny'
 *   pluralCs(7, ['nalezen', 'nalezeny', 'nalezeno']) → 'nalezeno'
 *
 * Forms tuple is `[singular, paucal (2-4), plural (5+ / 0)]`.
 */
export function pluralCs(n: number, forms: [string, string, string]): string {
  if (n === 1) return forms[0];
  if (n >= 2 && n <= 4) return forms[1];
  return forms[2];
}
